import React from 'react';
import classNames from 'styles/utils/classNames';
import { formatGEXValue } from 'utils/shared/format';

/**
 * Graphics for the explanation cards and the guide. Each one draws the live number it sits
 * beside, so a card never shows a value that differs from the board. No example data lives here.
 */

const pct = (value: number) => `${value.toFixed(1)}%`;
const money = (value: number) => `$${value.toFixed(2)}`;

/** Positive or negative gamma and the play on each side, with the board's current side lit. */
export function RegimeVisual({ netGEX }: { netGEX: number }) {
  const sides = [
    {
      key: 'positive',
      label: '+ Positive',
      read: 'Ranges hold',
      play: 'Fade the extremes',
      on: netGEX > 0,
    },
    {
      key: 'negative',
      label: '− Negative',
      read: 'Breaks run',
      play: 'Ride the break',
      on: netGEX < 0,
    },
  ];
  return (
    <div
      className="grid grid-cols-2 gap-1.5"
      role="img"
      aria-label={`Net GEX ${formatGEXValue(netGEX)}: ${
        netGEX > 0
          ? 'positive gamma, ranges hold'
          : netGEX < 0
          ? 'negative gamma, breaks run'
          : 'flat'
      }`}
    >
      {sides.map((side) => (
        <div
          key={side.key}
          className={classNames(
            'rounded-lg px-2.5 py-1 ring-1 ring-inset',
            side.on
              ? side.key === 'positive'
                ? 'bg-bullish-green/[0.14] ring-bullish-green/50'
                : 'bg-graph-red/[0.14] ring-graph-red/50'
              : 'bg-white/[0.03] ring-white/[0.06]',
          )}
        >
          <div className="flex items-center justify-between gap-1">
            <span
              className={classNames(
                'text-[11px] font-bold',
                side.on
                  ? side.key === 'positive'
                    ? 'text-bullish-green'
                    : 'text-graph-red'
                  : 'text-text-faded',
              )}
            >
              {side.label}
            </span>
            {side.on && (
              <span className="text-[9px] font-bold uppercase tracking-wider text-text-primary">
                Now
              </span>
            )}
          </div>
          <span
            className={classNames(
              'block text-[11px] leading-4',
              side.on ? 'text-text-primary' : 'text-text-faded',
            )}
          >
            {side.read}
          </span>
          <span
            className={classNames(
              'block text-[11px] font-semibold leading-4',
              side.on ? 'text-text-primary' : 'text-text-faded',
            )}
          >
            {side.play}
          </span>
        </div>
      ))}
    </div>
  );
}

/** Call and put gamma as two bars on one scale. */
export function BalanceVisual({ callGEX, putGEX }: { callGEX: number; putGEX: number }) {
  const max = Math.max(Math.abs(callGEX), Math.abs(putGEX), 1);
  const rows = [
    { label: 'Calls', value: callGEX, bar: 'bg-bullish-green/70', text: 'text-bullish-green' },
    { label: 'Puts', value: putGEX, bar: 'bg-graph-red/70', text: 'text-graph-red' },
  ];
  return (
    <div
      className="space-y-1.5"
      role="img"
      aria-label={`Call GEX ${formatGEXValue(callGEX)}, Put GEX ${formatGEXValue(putGEX)}`}
    >
      {rows.map((row) => (
        <div key={row.label} className="flex items-center gap-2 text-[11px]">
          <span className="w-9 shrink-0 text-text-secondary">{row.label}</span>
          <span className="h-1.5 flex-1 overflow-hidden rounded-full bg-white/[0.06]">
            <span
              className={classNames('block h-full rounded-full', row.bar)}
              style={{ width: `${(Math.abs(row.value) / max) * 100}%` }}
            />
          </span>
          <span
            className={classNames('w-14 shrink-0 text-right font-semibold tabular-nums', row.text)}
          >
            {formatGEXValue(row.value)}
          </span>
        </div>
      ))}
    </div>
  );
}

export interface RailLevel {
  key: string;
  label: string;
  price: number;
  tone: 'call' | 'put' | 'zero' | 'peak';
}

const railTone: Record<RailLevel['tone'], string> = {
  call: 'bg-bullish-green',
  put: 'bg-graph-red',
  zero: 'bg-graph-yellow',
  peak: 'bg-accent-blue',
};
const railText: Record<RailLevel['tone'], string> = {
  call: 'text-bullish-green',
  put: 'text-graph-red',
  zero: 'text-graph-yellow',
  peak: 'text-accent-blue',
};

/**
 * The levels around price at their real distances. The highlighted level is named above the
 * rail with its price and distance; spot is named below it.
 */
export function LevelRail({
  spot,
  levels,
  highlight,
}: {
  spot: number;
  levels: RailLevel[];
  /** The level to name; defaults to the nearest one. */
  highlight?: string;
}) {
  const known = levels.filter((level) => Number.isFinite(level.price) && level.price > 0);
  if (!(spot > 0) || known.length === 0) return null;
  const focus =
    known.find((level) => level.key === highlight) ??
    [...known].sort((a, b) => Math.abs(a.price - spot) - Math.abs(b.price - spot))[0];
  const prices = [spot, ...known.map((level) => level.price)];
  const low = Math.min(...prices);
  const high = Math.max(...prices);
  const pad = (high - low || spot * 0.01) * 0.08;
  const x = (price: number) => ((price - (low - pad)) / (high - low + pad * 2)) * 100;
  const distance = ((focus.price - spot) / spot) * 100;
  const place = (at: number): React.CSSProperties => ({
    left: `${at}%`,
    transform: `translateX(-${at}%)`,
  });
  return (
    <div
      className="relative h-[42px]"
      role="img"
      aria-label={`${focus.label} ${money(focus.price)}, ${
        distance > 0 ? '+' : ''
      }${distance.toFixed(2)}% from spot ${money(spot)}`}
    >
      <span
        className="absolute top-0 whitespace-nowrap text-[11px] font-semibold leading-4"
        style={place(x(focus.price))}
      >
        <span className={railText[focus.tone]}>{focus.label}</span>{' '}
        <span className="tabular-nums text-text-primary">{money(focus.price)}</span>{' '}
        <span className="tabular-nums text-text-secondary">
          {distance > 0 ? '+' : ''}
          {distance.toFixed(2)}%
        </span>
      </span>
      <span className="absolute inset-x-0 top-[21px] h-px bg-white/[0.12]" />
      {known.map((level) => (
        <span
          key={level.key}
          className={classNames(
            'absolute top-[21px] -translate-x-1/2 -translate-y-1/2 rounded-full',
            railTone[level.tone],
            level.key === focus.key ? 'h-2.5 w-2.5 ring-2 ring-white/25' : 'h-1.5 w-1.5 opacity-60',
          )}
          style={{ left: `${x(level.price)}%` }}
        />
      ))}
      <span
        className="absolute top-[21px] h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-text-primary bg-palette-black-1"
        style={{ left: `${x(spot)}%` }}
      />
      <span
        className="absolute bottom-0 whitespace-nowrap text-[11px] leading-4 text-text-secondary"
        style={place(x(spot))}
      >
        Spot <span className="tabular-nums text-text-primary">{money(spot)}</span>
      </span>
    </div>
  );
}

/** The share of the chain's gamma in the strip's three windows: today, 7 days, and 30 days. */
export function WindowBars({
  zeroDte,
  weekly,
  monthly,
}: {
  zeroDte: number;
  weekly: number;
  monthly: number;
}) {
  const rows = [
    { label: '0DTE', value: zeroDte },
    { label: 'Weekly · 7 days', value: weekly },
    { label: 'Monthly · 30 days', value: monthly },
  ];
  return (
    <div
      className="space-y-1"
      role="img"
      aria-label={rows.map((row) => `${row.label} ${pct(row.value)}`).join(', ')}
    >
      {rows.map((row) => (
        <div key={row.label} className="flex items-center gap-2 text-[11px]">
          <span className="w-[104px] shrink-0 text-text-secondary">{row.label}</span>
          <span className="h-1.5 flex-1 overflow-hidden rounded-full bg-white/[0.06]">
            <span
              className="block h-full rounded-full bg-accent-blue/80"
              style={{ width: `${Math.max(0, Math.min(100, row.value))}%` }}
            />
          </span>
          <span className="w-11 shrink-0 text-right font-semibold tabular-nums text-text-primary">
            {pct(row.value)}
          </span>
        </div>
      ))}
    </div>
  );
}

/** The horizon choices and the calendar window each one covers. */
export function HorizonChips() {
  const chips = [
    ['Today', '0d'],
    ['1W', '7d'],
    ['1M', '30d'],
    ['3M', '90d'],
    ['6M', '180d'],
    ['All', 'every date'],
  ];
  return (
    <div className="flex flex-wrap gap-1">
      {chips.map(([label, span]) => (
        <span
          key={label}
          className="rounded-md bg-white/[0.04] px-1.5 py-0.5 text-[10px] text-text-secondary ring-1 ring-inset ring-white/[0.06]"
        >
          <span className="font-bold text-text-primary">{label}</span> {span}
        </span>
      ))}
    </div>
  );
}

/** The screener's four labels and the scores they start at (calculateGammaSqueezeScore). */
const SETUP_BANDS = [
  { label: 'Unlikely', from: 0, to: 30 },
  { label: 'Possible', from: 30, to: 50 },
  { label: 'Likely', from: 50, to: 75 },
  { label: 'Imminent', from: 75, to: 101 },
] as const;

const setupBand = (score: number) =>
  SETUP_BANDS.find((band) => score >= band.from && score < band.to)?.label ?? 'Unlikely';

/** The four labels with the live score marked in its band. */
export function BandMeter({ score }: { score: number }) {
  const current = setupBand(score);
  const clamped = Math.max(0, Math.min(100, score));
  return (
    <div role="img" aria-label={`Probability score ${score} of 100: ${current}`}>
      <div className="relative flex h-1.5 gap-0.5">
        {SETUP_BANDS.map((band) => (
          <span
            key={band.label}
            className={classNames(
              'h-full rounded-full',
              band.label === current ? 'bg-accent-blue' : 'bg-white/[0.08]',
            )}
            style={{ width: `${Math.min(band.to, 100) - band.from}%` }}
          />
        ))}
        <span
          className="absolute -top-[3px] h-3 w-0.5 -translate-x-1/2 rounded-full bg-text-primary"
          style={{ left: `${clamped}%` }}
        />
      </div>
      <div className="mt-1 flex text-[10px]">
        {SETUP_BANDS.map((band) => (
          <span
            key={band.label}
            className={classNames(
              band.label === current ? 'font-bold text-text-primary' : 'text-text-faded',
            )}
            style={{ width: `${Math.min(band.to, 100) - band.from}%` }}
          >
            {band.label}
          </span>
        ))}
      </div>
    </div>
  );
}

/** The loaded smile at three points: the 25-delta put wing, ATM, and the 25-delta call wing. */
export function SmileVisual({
  putIV,
  atmIV,
  callIV,
}: {
  putIV: number;
  atmIV: number;
  callIV: number;
}) {
  const values = [putIV, atmIV, callIV];
  const low = Math.min(...values);
  const high = Math.max(...values);
  const y = (iv: number) => (high === low ? 22 : 34 - ((iv - low) / (high - low)) * 26);
  const points = [
    { x: 24, iv: putIV, label: '25Δ put' },
    { x: 130, iv: atmIV, label: 'ATM' },
    { x: 236, iv: callIV, label: '25Δ call' },
  ];
  const [p0, p1, p2] = points.map((point) => ({ x: point.x, y: y(point.iv) }));
  // A quadratic curve that passes through the ATM point.
  const control = { x: 2 * p1.x - (p0.x + p2.x) / 2, y: 2 * p1.y - (p0.y + p2.y) / 2 };
  return (
    <div
      role="img"
      aria-label={`25 delta put ${pct(putIV)}, ATM ${pct(atmIV)}, 25 delta call ${pct(callIV)}`}
    >
      <svg viewBox="0 0 260 40" className="h-10 w-full" aria-hidden="true">
        <path d={`M16 ${p1.y}H244`} stroke="rgba(255,255,255,0.12)" strokeDasharray="3 3" />
        <path
          d={`M${p0.x} ${p0.y}Q${control.x} ${control.y} ${p2.x} ${p2.y}`}
          fill="none"
          stroke="#45a0fa"
          strokeWidth="2.5"
          strokeLinecap="round"
        />
        {[p0, p1, p2].map((point, index) => (
          <circle key={index} cx={point.x} cy={point.y} r={index === 1 ? 3.5 : 3} fill="#45a0fa" />
        ))}
      </svg>
      <div className="mt-0.5 grid grid-cols-3 text-[10px]">
        {points.map((point, index) => (
          <span
            key={point.label}
            className={classNames(
              index === 0 ? 'text-left' : index === 1 ? 'text-center' : 'text-right',
              'text-text-secondary',
            )}
          >
            {point.label}{' '}
            <span className="font-semibold tabular-nums text-text-primary">{pct(point.iv)}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

/** What the IV shift does to the chain's ATM IV. */
export function IVShiftVisual({ atmIV, adjustment }: { atmIV: number; adjustment: number | null }) {
  const shift = adjustment && adjustment !== 0 ? adjustment : null;
  return (
    <div className="flex items-center justify-between gap-2 rounded-lg bg-white/[0.03] px-3 py-2 text-[11px] ring-1 ring-inset ring-white/[0.06]">
      <span className="text-text-secondary">
        ATM IV <span className="font-semibold tabular-nums text-text-primary">{pct(atmIV)}</span>
      </span>
      {shift === null ? (
        <span className="font-semibold text-text-secondary">Market</span>
      ) : (
        <>
          <span className="font-semibold text-accent-blue">
            {shift > 0 ? '+' : ''}
            {(shift * 100).toFixed(0)}% →
          </span>
          <span className="font-semibold tabular-nums text-text-primary">
            {pct(atmIV * (1 + shift))}
          </span>
        </>
      )}
    </div>
  );
}

export interface OptionCell {
  label: string;
  claim: string;
  active: boolean;
}

/** The choices a control offers, with the active one lit. */
export function OptionCells({ options }: { options: OptionCell[] }) {
  return (
    <div
      className={classNames('grid gap-1.5', options.length === 3 ? 'grid-cols-3' : 'grid-cols-2')}
    >
      {options.map((option) => (
        <div
          key={option.label}
          data-gex-option
          className={classNames(
            'rounded-lg px-2.5 py-1 ring-1 ring-inset',
            option.active
              ? 'bg-accent-blue/[0.12] ring-accent-blue/45'
              : 'bg-white/[0.03] ring-white/[0.06]',
          )}
        >
          <span
            className={classNames(
              'block text-[11px] font-bold',
              option.active ? 'text-text-primary' : 'text-text-secondary',
            )}
          >
            {option.label}
          </span>
          <span className="block text-[11px] leading-4 text-text-secondary">{option.claim}</span>
          {option.active && <span className="sr-only">Selected</span>}
        </div>
      ))}
    </div>
  );
}
